import {
  BookOpen,
  BookUser,
  CirclePlus,
  Heart,
  User,
  Users,
  type LucideIcon,
} from "lucide-react"

import type { TrailCrumb } from "@/components/page-chrome"
import type { BarePage, PageLayout } from "@/components/page-chrome/page-layout"
import type {
  WorkspaceKey,
  WorkspaceNamespace,
} from "@/components/workspace/page-chrome"
import { ROUTES } from "@/constants"

import type {
  SearchReturn,
  SearchReturnIndex,
} from "./search-return-navigation"

// A search page with the band, whose trail starts at Search.
export type SearchTrailPage<Key extends PropertyKey = string> = {
  layout: Exclude<PageLayout, "bare">
  icon: LucideIcon
  title: WorkspaceKey
  // Where a canonical trail links this page when it is an ancestor.
  href?: string
  // The page whose crumb comes before this one when the page has no return
  // record: a record page's type index, or the index a page belongs to.
  parent?: Key
  // The results this page shows, which its type crumb names.
  index?: SearchReturnIndex
  // The page is the Search root: Search is its only crumb, with no Back.
  root?: true
  // The type of the record the page shows, for its hop's "Name (Type)".
  record?: WorkspaceKey
}

// A registry entry: a page with a trail, or a bare page (one opened in a
// sheet) that carries its layout alone.
export type SearchPage<Key extends PropertyKey = string> =
  | SearchTrailPage<Key>
  | BarePage

export type SearchPages<T> = { [K in keyof T]: SearchPage<keyof T> }

// The const type parameter keeps each parent as its literal, so a parent
// that is not a key of the registry fails to compile.
export function defineSearchPages<const T extends SearchPages<T>>(pages: T): T {
  return pages
}

// The search pages, keyed by Inertia component name. The three results
// pages are the Search root, their types the tabs in the card. A record page
// is reached through results or other record pages; its trail is the path
// the user took, and its parent the type index it falls back to. The
// shortlist entry pages open in a sheet and walk no trail.
export const searchPages = defineSearchPages({
  "search/classes/index": {
    layout: "standard",
    icon: BookOpen,
    title: "search:tabs.classes",
    href: "/search/classes",
    index: "classes",
    root: true,
  },
  "search/educators/index": {
    layout: "standard",
    icon: Users,
    title: "search:tabs.educators",
    href: "/search/educators",
    index: "educators",
    root: true,
  },
  "search/relief_listings/index": {
    layout: "standard",
    icon: BookUser,
    title: "search:tabs.relief_listings",
    href: "/search/relief_listings",
    index: "relief_listings",
    root: true,
  },
  "search/shortlists/index": {
    layout: "standard",
    icon: Heart,
    title: "shortlists:index.title",
    href: "/search/shortlists",
    index: "shortlists",
  },
  "search/shortlists/new": {
    layout: "standard",
    icon: CirclePlus,
    title: "shortlists:new.title",
    parent: "search/shortlists/index",
  },
  "search/shortlists/classes/index": {
    layout: "standard",
    icon: Heart,
    title: "shortlists:workspace.title",
    parent: "search/shortlists/index",
    record: "shortlists:workspace.title",
  },
  "search/shortlists/educators/index": {
    layout: "standard",
    icon: Heart,
    title: "shortlists:workspace.title",
    parent: "search/shortlists/index",
    record: "shortlists:workspace.title",
  },
  "search/classes/show": {
    layout: "standard",
    icon: BookOpen,
    title: "search:show.class.title",
    parent: "search/classes/index",
    record: "search:records.class",
  },
  "search/educators/show.teacher": {
    layout: "standard",
    icon: User,
    title: "search:show.teacher.title",
    parent: "search/educators/index",
    record: "search:records.teacher",
  },
  "search/educators/show.team": {
    layout: "standard",
    icon: Users,
    title: "search:show.team.title",
    parent: "search/educators/index",
    record: "search:records.team",
  },
  "search/relief_listings/show.educator": {
    layout: "standard",
    icon: BookUser,
    title: "search:show.educator.relief_listing.title",
    parent: "search/relief_listings/index",
    record: "search:records.relief_listing",
  },
  "search/relief_listings/show.learner": {
    layout: "standard",
    icon: BookUser,
    title: "search:show.learner.relief_listing.title",
    parent: "search/relief_listings/index",
    record: "search:records.relief_listing",
  },
  "search/classes/shortlist_entries/new": { layout: "bare" },
  "search/educators/shortlist_entries/new": { layout: "bare" },
})

export type SearchPageName = keyof typeof searchPages

const registry: Record<string, SearchPage> = searchPages

function entryOf(pages: Record<string, SearchPage>, page: string) {
  if (!Object.hasOwn(pages, page)) {
    throw new Error(`${page} is not a registered search page`)
  }
  return pages[page]
}

// The entry of a page with a trail. Raises when `page` is not registered or
// is a bare page, which has no trail to walk.
function trailEntryOf(
  pages: Record<string, SearchPage>,
  page: string,
): SearchTrailPage {
  const entry = entryOf(pages, page)
  if (entry.layout === "bare") {
    throw new Error(`${page} is a bare page with no trail`)
  }
  return entry
}

const trailEntries = (pages: Record<string, SearchPage>) =>
  Object.values(pages).filter(
    (entry): entry is SearchTrailPage => entry.layout !== "bare",
  )

export function searchPageEntry(page: string): SearchTrailPage {
  return trailEntryOf(registry, page)
}

// The ancestors of `page` by parent, nearest last, without `page`. Raises on
// an unregistered page or a parent cycle.
function ancestorsOf(pages: Record<string, SearchPage>, page: string) {
  const ancestors: string[] = []
  let current = trailEntryOf(pages, page).parent
  while (current !== undefined) {
    if (current === page || ancestors.includes(current)) {
      throw new Error(`Search page ${page} has a parent cycle`)
    }
    ancestors.unshift(current)
    current = trailEntryOf(pages, current).parent
  }
  return ancestors
}

// The index a page falls back to: its own, else its nearest ancestor's.
function canonicalIndexOf(pages: Record<string, SearchPage>, page: string) {
  const index = [page, ...ancestorsOf(pages, page).reverse()]
    .map((name) => trailEntryOf(pages, name).index)
    .find((candidate) => candidate !== undefined)
  if (index === undefined) {
    throw new Error(`Search page ${page} belongs to no index`)
  }
  return index
}

// What following a search link from `page` starts from: a results page
// starts a trail; a record page adds itself as a hop, named by `recordName`;
// any other page passes its trail on.
export type SearchOrigin =
  | { kind: "results"; index: SearchReturnIndex }
  | { kind: "record"; index: SearchReturnIndex; page: string; name: string }
  | { kind: "page"; index: SearchReturnIndex }

export function searchOriginOf(
  page: string,
  recordName: (page: string) => string,
  pages: Record<string, SearchPage> = registry,
): SearchOrigin {
  const entry = trailEntryOf(pages, page)
  if (entry.index !== undefined && entry.record === undefined) {
    return { kind: "results", index: entry.index }
  }
  const index = canonicalIndexOf(pages, page)
  if (entry.record !== undefined) {
    return { kind: "record", index, page, name: recordName(page) }
  }
  return { kind: "page", index }
}

// Every namespace a search trail can label a crumb from, since a hop may
// name any record page's type.
export function searchPageNamespaces(
  pages: Record<string, SearchPage> = registry,
): WorkspaceNamespace[] {
  const keys = trailEntries(pages).flatMap(({ title, record }) =>
    record === undefined ? [title] : [title, record],
  )
  const namespaces = [...keys, "nav:search" as const].map(
    (key) => key.slice(0, key.indexOf(":")) as WorkspaceNamespace,
  )
  return [...new Set(namespaces)]
}

function indexCrumb(
  pages: Record<string, SearchPage>,
  index: SearchReturnIndex,
  href: string,
  t: (key: WorkspaceKey) => string,
) {
  const entry = trailEntries(pages).find(
    (candidate) => candidate.index === index && candidate.record === undefined,
  )
  if (entry === undefined) {
    throw new Error(`No search page shows the ${index} results`)
  }
  return { label: t(entry.title), href }
}

// The record type a hop's page shows: none when the page is not registered
// or has no trail, which sends the trail back to its canonical parents.
function hopType(pages: Record<string, SearchPage>, page: string) {
  if (!Object.hasOwn(pages, page)) return undefined
  const entry = pages[page]
  return entry.layout === "bare" ? undefined : entry.record
}

// The crumbs before `page`. A root page has none. Every other trail starts
// at Search. A record page reached with a return record then names the
// results left, a "…" when older hops were folded away, and each hop as
// "Name (Type)", so its parent, and Back, is the page it was reached from.
// Without a record, or with one naming a page the registry no longer has,
// a page walks its canonical parents: Search › type › page.
export function searchPageCrumbs(
  page: string,
  {
    searchReturn,
    t,
    pages = registry,
  }: {
    searchReturn: SearchReturn | null
    t: (key: WorkspaceKey) => string
    pages?: Record<string, SearchPage>
  },
): TrailCrumb[] {
  const entry = trailEntryOf(pages, page)
  if (entry.root) return []

  const search = { label: t("nav:search"), href: ROUTES.SEARCH }

  const hopTypes = searchReturn?.hops.map((hop) => hopType(pages, hop.page))
  if (
    entry.record !== undefined &&
    searchReturn !== null &&
    hopTypes !== undefined &&
    hopTypes.every((type) => type !== undefined)
  ) {
    return [
      search,
      indexCrumb(pages, searchReturn.index, searchReturn.target, t),
      ...(searchReturn.folded ? [{ folded: true as const }] : []),
      ...searchReturn.hops.map((hop, position) => ({
        label: hop.name,
        type: t(hopTypes[position]),
        href: hop.href,
      })),
    ]
  }

  return [
    search,
    ...ancestorsOf(pages, page).map((name) => {
      const { title, href } = trailEntryOf(pages, name)
      if (href === undefined) {
        throw new Error(`Search page ${name} is an ancestor with no href`)
      }
      return { label: t(title), href }
    }),
  ]
}
